import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material';
import {
  CreateAccountCommand,
  EventCountCommand,
} from '../../../commands/accounts/create.account.command';
import { ButtonTriggerEvent } from '../../../common/components';
import { ConfirmModalComponent } from '../../../common/components/confirm.modal/confirm.modal.component';
import {
  ConfirmModalActions,
  ConfirmModalData,
} from '../../../common/components/confirm.modal/confirm.modal.provider';
import { SuccessWrapComponent } from '../../../common/components/success.wrap/success.wrap.component';
import { ModalService } from '../../../common/services/modal.service';
import { Account } from '../../../models/admin/account';
import { HubVisitorFeatureToggle } from '../../../models/hub/hub.visitor.feature.toggle';
import { AccountApiService } from '../../../services/account.api.service';
import { AccountUsersModalComponent } from './account.users/account.users.modal.component';
import {
  AccountUsersModalActions,
  AccountUsersModalData,
} from './account.users/account.users.modal.provider';
import { EventSpiderModalComponent } from './event.spider/event.spider.modal.component';
import {
  EventSpiderModalData,
  EventSpiderModalActions,
} from './event.spider/event.spider.modal.provider';
import { ShowInfoModalComponent } from '../../hub/show.info.modal/show.info.modal.component';
import {
  InfoModalData,
  ShowInfoModalActions,
} from '../../hub/show.info.modal/show.info.modal.provider';
import { SetApprovedComponent } from './set.approved/set.approved.component';
import { SetContactsModalComponent } from './set.contacts/set.contacts.modal.component';
import {
  SetContactsModalActions,
  SetContactsModalData,
} from './set.contacts/set.contacts.modal.provider';
import { SetPlaceSourceModalComponent } from './set.place.source/set.place.source.modal.component';
import {
  SetPlaceSourceModalData,
  SetPlaceSourceModalActions,
} from './set.place.source/set.place.source.modal.provider';
import { ShowPlacesCountModalComponent } from './show.places.count/show.places.count.modal.component';
import {
  ShowPlacesCountModalActions,
  ShowPlacesCountModalData,
} from './show.places.count/show.places.count.modal.provider';
import { ToggleFeaturesModalComponent } from './toggle.features/toggle.features.modal.component';
import {
  ToggleFeaturesModalActions,
  ToggleFeaturesModalData,
} from './toggle.features/toggle.features.modal.provider';
import { ToggleIsClientModalComponent } from './toggle.is.client/toggle.is.client.modal.component';
import {
  ToggleIsClientModalActions,
  ToggleIsClientModalData,
} from './toggle.is.client/toggle.is.client.modal.provider';
import { SetIgVersionModalComponent } from './set.ig.version/set.ig.version.modal.component';
import {
  SetIgVersionModalActions,
  SetIgVersionModalData,
} from './set.ig.version/set.ig.version.modal.provider';
import { TravelBuddySettingsModalComponent } from './travel.buddy.settings/travel.buddy.settings.modal.component';
import {
  TravelBuddySettingsModalActions,
  TravelBuddySettingsModalData,
} from './travel.buddy.settings/travel.buddy.settings.modal.provider';
import { isNullOrUndefined } from '../../../common/extensions';
import { ConfigService } from '../../../services/config.service';
import { AppModeService } from '../../../common/services/app.mode.service';


@Component({
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.scss'],
})
export class AccountsComponent implements OnInit {
  public accounts: Account[];
  public liveCount = 0;
  public deadCount = 0;
  public createAccountCommand = new CreateAccountCommand('', '');
  public createAccountFormGroup: FormGroup;
  @ViewChild('clientAccountSuccess') public clientAccountSuccess: SuccessWrapComponent;

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private modalService: ModalService,
    private accountApiService: AccountApiService
  ) {}

  public ngOnInit() {
    this.createAccountFormGroup = this.formBuilder.group({
      userName: [null, [Validators.required]],
      password: [null, [Validators.required]],
    });

    this.fetchAccounts();
  }

  public createAccount($event: ButtonTriggerEvent) {
    this.accountApiService
      .createAccount(this.createAccountCommand)
      .finally($event.complete)
      .subscribe(() => {
        this.createAccountCommand = new CreateAccountCommand('', '');
        this.createAccountFormGroup.markAsPristine();
        this.fetchAccounts();
      });
  }

  public deleteAccount(account: Account) {
    let actions = <ConfirmModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
        this.accountApiService
          .deleteAccount(account._id)
          .finally(event.complete)
          .subscribe(() => {
            this.fetchAccounts();
          });
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription: 'Are you sure you want to delete this account?',
      confirmModalConfirmButtonText: 'Delete',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  public disableAccount(account: Account) {
    let actions = <ConfirmModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
        this.accountApiService
          .disableAccount(account._id)
          .finally(event.complete)
          .subscribe(() => {
            this.fetchAccounts();
          });
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription:
        'Are you sure you want to disable this account, but you can enable it later?',
      confirmModalConfirmButtonText: 'Disable',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  public enableAccount(account: Account) {
    let actions = <ConfirmModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
        this.accountApiService
          .enableAccount(account._id)
          .finally(event.complete)
          .subscribe(() => {
            this.fetchAccounts();
          });
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription: 'Are you sure you want to enable this account?',
      confirmModalConfirmButtonText: 'Enable',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  public SetDefaultApproved(account: Account) {
    const dialogConfig = new MatDialogConfig();
    
    dialogConfig.data = {
      eventApproved:
        account.customSettings 
        && account.customSettings.defaultSetting 
        && account.customSettings.defaultSetting.event
          ? (isNullOrUndefined(account.customSettings.defaultSetting.event.isApproved) 
            ? false 
            : account.customSettings.defaultSetting.event.isApproved)
          : false,

      placeApproved:
        account.customSettings 
        && account.customSettings.defaultSetting 
        && account.customSettings.defaultSetting.place  
          ? (isNullOrUndefined(account.customSettings.defaultSetting.place.isApproved) 
            ? true 
            : account.customSettings.defaultSetting.place.isApproved)
          : true,
      jobApproved:
        account.customSettings &&
        account.customSettings.defaultSetting &&
        !isNullOrUndefined(account.customSettings.defaultSetting.job)
          ? account.customSettings.defaultSetting.job.isApproved
          : true,
    };
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    let dialogRef = this.dialog.open(SetApprovedComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.accountApiService
          .SetDefaultApproved(account._id, data.eventApproved, data.placeApproved, data.jobApproved)
          .subscribe(() => {
            this.fetchAccounts();
          });
      }
    });
  }

  public async getAPIKey(account: Account) {
    if (!account.apiKey || !account.apiKey.key) {
      const data = await this.accountApiService.setApiKey(account._id).toPromise();
      account.apiKey = data;
    }

    const info = [];
    info.push(
      `<b>Key:</b> <input style="width:450px;height:30px" value="${account.apiKey.key}"></input>`
    );
    info.push(
      `<b>JSON feed last request time:</b> ${account.apiKey.lastRequest.time}, <b>with key:</b> ${
        account.apiKey.lastRequest.isContainKey
          ? '<span style="color:green">Yes</span>'
          : '<span style="color:red">No</span>'
      }`
    );

    this.modalService.show({
      component: ShowInfoModalComponent,
      providers: [
        { provide: ShowInfoModalActions, useValue: null },
        { provide: InfoModalData, useValue: new InfoModalData('API Key', 'attach_money', info) },
      ],
      panelClass: 'width-modal-4',
    });
  }

  public changeIgVersion(account: Account) {
    this.modalService.show({
      component: SetIgVersionModalComponent,
      providers: [
        { provide: SetIgVersionModalActions, useValue: new SetIgVersionModalActions() },
        { provide: SetIgVersionModalData, useValue: new SetIgVersionModalData(account) },
      ],
      panelClass: 'width-modal-4',
    });
  }

  public travelBuddySettings(account: Account) {
    this.modalService.show({
      component: TravelBuddySettingsModalComponent,
      providers: [
        {
          provide: TravelBuddySettingsModalActions,
          useValue: new TravelBuddySettingsModalActions(),
        },
        {
          provide: TravelBuddySettingsModalData,
          useValue: new TravelBuddySettingsModalData(account),
        },
      ],
      panelClass: 'width-modal-5',
    });
  }

  public SetClientContacts(account: Account) {
    if (!account.contacts) {
      account.contacts = {
        name: '',
        number: '',
        email: '',
        inquiryEmail: '',
        eventsSsrEnabled: false,
        placesSsrEnabled: false,
        eventCalendarURL: '',
        placeCalendarURL: '',
        moreToursURL: '',
        placeURLs: [],
      };
    }
    if (!account.webhookSettings) {
      account.webhookSettings = {
        apiUserCacheWebhook: {
          enabled: false,
          endpointUrl: '',
          signingSecret: '',
          sendEventDetailInvalidation: true,
          sendEventListingInvalidation: true,
          sendPlaceDetailInvalidation: true,
          sendPlaceListingInvalidation: true,
          sendSsrModuleStatusUpdate: true,
        },
      };
    }
    this.modalService.show({
      component: SetContactsModalComponent,
      providers: [
        { provide: SetContactsModalActions, useValue: new SetContactsModalActions() },
        { provide: SetContactsModalData, useValue: new SetContactsModalData(account) },
      ],
      panelClass: 'width-modal-70p',
    });
  }

  public toggleClientAccount(account: Account) {
    this.modalService.show({
      component: ToggleIsClientModalComponent,
      providers: [
        { provide: ToggleIsClientModalActions, useValue: new ToggleIsClientModalActions() },
        { provide: ToggleIsClientModalData, useValue: new ToggleIsClientModalData(account) },
      ],
    });
  }

  public getPlaceCount(account: Account) {
    this.modalService.show({
      component: ShowPlacesCountModalComponent,
      providers: [
        { provide: ToggleIsClientModalActions, useValue: new ShowPlacesCountModalActions() },
        { provide: ShowPlacesCountModalData, useValue: new ShowPlacesCountModalData(account) },
      ],
      panelClass: 'width-modal-70p',
    });
    // this.accountApiService.getPlaceCount(account.userName)
    //     .subscribe((count) => {
    //         if (count && !isNullOrUndefined(count.total) && !isNullOrUndefined(count.approved) && !isNullOrUndefined(count.unApproved)) {
    //             this.modalService.show({
    //                 component: ShowInfoModalComponent,
    //                 providers: [
    //                     {provide: ShowInfoModalActions, useValue: null},
    //                     {
    //                         provide: InfoModalData,
    //                         useValue: new InfoModalData(`Place Count for ${account.userName}`, [], [`Total place count: ${count.total}`, `<span style="color:green">Approved: ${count.approved}</span>`, `<span style="color:red">UnApproved: ${count.unApproved}</span>`])
    //                     }
    //                 ]
    //             });
    //         }
    //     });
  }

  public setPlaceSource(account: Account) {
    this.modalService.show({
      component: SetPlaceSourceModalComponent,
      providers: [
        { provide: SetPlaceSourceModalActions, useValue: new SetPlaceSourceModalActions() },
        { provide: SetPlaceSourceModalData, useValue: new SetPlaceSourceModalData(account) },
      ],
      // panelClass: 'width-modal-4',
    });
  }

  public showUsers(account: Account) {
    this.modalService.show({
      component: AccountUsersModalComponent,
      providers: [
        { provide: AccountUsersModalActions, useValue: new AccountUsersModalActions() },
        { provide: AccountUsersModalData, useValue: new AccountUsersModalData(account.userName) },
      ],
      panelClass: 'width-modal-70p',
    });
  }

  public eventSpider(account: Account) {
    if (!account.toggle) {
      account.toggle = new HubVisitorFeatureToggle();
    }
    this.modalService.show({
      component: EventSpiderModalComponent,
      providers: [
        { provide: EventSpiderModalActions, useValue: new EventSpiderModalActions() },
        { provide: EventSpiderModalData, useValue: new EventSpiderModalData(account) },
      ],
      panelClass: 'width-modal-70p',
    });
  }

  public toggleFeatures(account: Account) {
    if (!account.toggle) {
      account.toggle = new HubVisitorFeatureToggle();
    }
    this.modalService.show({
      component: ToggleFeaturesModalComponent,
      providers: [
        { provide: ToggleFeaturesModalActions, useValue: new ToggleFeaturesModalActions() },
        { provide: ToggleFeaturesModalData, useValue: new ToggleFeaturesModalData(account) },
      ],
      panelClass: 'width-modal-1',
    });
  }

  public showEventCount(account: Account) {
    this.accountApiService.getEventCount(account.userName).subscribe((count: EventCountCommand) => {
      let info = [
        `Total Approved Event Count: <b style="color:green">${count.approved}</b>`,
        `Total Unapproved Event Count: <b style="color:red">${count.unapproved}</b>`,
      ];

      let monthly = '',
        annual = {};
      if (count.spiderMonthlyCount && count.spiderMonthlyCount.length) {
        const monthText = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        count.spiderMonthlyCount.forEach((x) => {
          if (x.month.indexOf('-') >= 0) {
            const year = parseInt(x.month.split('-')[0], 10);
            let monthIndex = parseInt(x.month.split('-')[1], 10) - 1;
            const monthTitle = `${monthText[monthIndex]}.${x.month.split('-')[0]}`;

            let total = x.facebook + x.eventbrite + (x.serpGoogle || 0) - (x.similarCount || 0);
            total = total < 0 ? 0 : total;
            let approvedPer =
              (x.approvedCount || 0) >= total ? 100 : ((x.approvedCount || 0) / total) * 100;
            const percentage = (approvedPer || 0).toFixed(0);

            if (monthIndex === 0 || !annual[year]) {
              annual[year] = {
                facebook: x.facebook,
                eventbrite: x.eventbrite,
                serpGoogle: x.serpGoogle || 0,
                approvedCount: {
                  count: x.approvedCount || 0,
                  rate: percentage + '%',
                  monthly: `${monthTitle}: <b>${
                    x.approvedCount || 0
                  }</b>, Total: <b>${total}</b>, Approval Rate: <b>${percentage}%</b><br>`,
                },
                similarCount: x.similarCount || 0,
                yearTotal: total,
              };
            } else {
              annual[year].facebook += x.facebook;
              annual[year].eventbrite += x.eventbrite;
              annual[year].serpGoogle += x.serpGoogle || 0;
              annual[year].approvedCount.count += x.approvedCount || 0;
              annual[year].similarCount += x.similarCount || 0;

              let yearTotal =
                annual[year].facebook +
                annual[year].eventbrite +
                annual[year].serpGoogle -
                annual[year].similarCount;
              yearTotal = yearTotal < 0 ? 0 : yearTotal;
              let rate =
                annual[year].approvedCount.count > yearTotal
                  ? 100
                  : (annual[year].approvedCount.count / yearTotal) * 100;
              annual[year].approvedCount.rate = rate.toFixed(0) + '%';
              annual[year].yearTotal = yearTotal;

              annual[year].approvedCount.monthly += `${monthTitle}: <b>${
                x.approvedCount || 0
              }</b>, Total: <b>${total}</b>, Approval Rate: <b>${percentage}%</b><br>`;
            }

            monthly += `${monthTitle}: <b>${
              x.facebook + x.eventbrite + (x.serpGoogle || 0)
            }</b> in all (Facebook: ${x.facebook}, Eventbrite: ${x.eventbrite}, SerpGoogle: ${
              x.serpGoogle || 0
            }, similar: ${x.similarCount || 0})<br>`;
          }
        });
        info.push(
          `<span style="color:mediumblue;cursor:pointer" onclick="var countwrap=document.getElementById('countwrap');countwrap.style.visibility=countwrap.style.visibility==='hidden'?'visible':'hidden';countwrap.style.height=countwrap.style.height==='0px'?'auto':'0px'">Show monthly accumulated quantity</span>`
        );
        info.push(`<div id="countwrap" style="visibility: hidden;height:0px">${monthly}</div>`);
      }

      info.push(
        ...[
          '<br/>',
          `<b>Facebook Event Count</b>`,
          `Total Approved Event Count: <b style="color:green">${count.fbPage.approved}</b>, Total Unapproved Event Count: <b style="color:red">${count.fbPage.unapproved}</b>`,
        ]
      );
      if (count.lastRunTime && count.lastRunTime.facebookLastRunTime) {
        let time = new Date(count.lastRunTime.facebookLastRunTime);
        info.push(`Last Run Time: <b>${time.toLocaleString()}</b>`);
      }
      for (let page of count.fbPage.pages) {
        info.push(
          `${page.url}: <b>${
            page.lastRunTime ? new Date(page.lastRunTime).toLocaleString() : 'N/A'
          }</b>, New events pulled: <b>${
            page.newEventsPulledInLastRun || 0
          }</b>, Existed Events: <b>${page.existedEventPulledInLastRun || 0}</b><br>`
        );
      }
      // info.push('<b>Facebook partners with no events:</b>');
      // for (let page of count.fbPage.noEventPages) {
      //   info.push(`${page}`);
      // }
      // let pageLastRunTime = '';
      // count.fbPage.pages.forEach((x) => {
      //   pageLastRunTime += `${x.url}: <b>${x.lastRunTime ? new Date(x.lastRunTime).toLocaleString() : 'N/A'}</b><br>`;
      // });
      // info.push(
      //   `<span style="color:mediumblue;cursor:pointer" onclick="var lastRunTimeByPage=document.getElementById('lastRunTimeByPage');lastRunTimeByPage.style.visibility=lastRunTimeByPage.style.visibility==='hidden'?'visible':'hidden';lastRunTimeByPage.style.height=lastRunTimeByPage.style.height==='0px'?'auto':'0px'">Show last run time for each page</span>`
      // );
      // info.push(`<div id="lastRunTimeByPage" style="visibility: hidden;height:0px">${pageLastRunTime}</div>`);

      info.push('<br/>');
      info.push(`<b>Eventbrite Event Count</b>`);
      info.push(
        `Total Approved Event Count: <b style="color:green">${count.eventbritePage.approved}</b>, Total Unapproved Event Count: <b style="color:red">${count.eventbritePage.unapproved}</b>`
      );
      if (count.lastRunTime && count.lastRunTime.eventbriteLastRunTime) {
        let time = new Date(count.lastRunTime.eventbriteLastRunTime);
        info.push(`Last Run Time: <b>${time.toLocaleString()}</b>`);
      }
      // for (let page in count.eventbritePage.eventByPage) {
      //   if (count.eventbritePage.eventByPage[page]) {
      //     info.push(`${page}: <b>${count.eventbritePage.eventByPage[page].length}</b>`);
      //   }
      // }

      for (const page of count.eventbritePage.pages) {
        info.push(
          `${page.url}: <b>${
            page.lastRunTime ? new Date(page.lastRunTime).toLocaleString() : 'N/A'
          }</b>, New events pulled: <b>${
            page.newEventsPulledInLastRun || 0
          }</b>, Existed Events: <b>${page.existedEventPulledInLastRun || 0}</b><br>`
        );
      }

      let pageLastRunTimeEventbrite = '';
      count.eventbritePage.pages.forEach((x) => {
        pageLastRunTimeEventbrite += `${x.url}: <b>${
          x.lastRunTime ? new Date(x.lastRunTime).toLocaleString() : 'N/A'
        }</b><br>`;
      });
      // info.push(
      //   `<span style="color:mediumblue;cursor:pointer" onclick="var pageLastRunTimeEventbrite=document.getElementById('pageLastRunTimeEventbrite');pageLastRunTimeEventbrite.style.visibility=pageLastRunTimeEventbrite.style.visibility==='hidden'?'visible':'hidden';pageLastRunTimeEventbrite.style.height=pageLastRunTimeEventbrite.style.height==='0px'?'auto':'0px'">Show last run time for each page</span>`
      // );
      // info.push(
      //   `<div id="pageLastRunTimeEventbrite" style="visibility: hidden;height:0px">${pageLastRunTimeEventbrite}</div>`
      // );

      // info.push('<br/>');
      // info.push(`<b>Eventful Event Count</b>`);
      // info.push(
      //   `Total Approved Event Count: <b style="color:green">${count.eventful.approved}</b>, Total Unapproved Event Count: <b style="color:red">${count.eventful.unapproved}</b>`
      // );

      info.push('<br/>');
      info.push(`<b>Serp Google Event Count</b>`);
      info.push(
        `Total Approved Event Count: <b style="color:green">${count.serpGoogle.approved}</b>, Total Unapproved Event Count: <b style="color:red">${count.serpGoogle.unapproved}</b>`
      );
      if (count.lastRunTime && count.lastRunTime.serpGoogleLastRunTime) {
        let time = new Date(count.lastRunTime.serpGoogleLastRunTime);
        info.push(`Last Run Time: <b>${time.toLocaleString()}</b>`);
      }

      for (const page of count.serpGoogle.pages) {
        info.push(
          `${page.url}: <b>${
            page.lastRunTime ? new Date(page.lastRunTime).toLocaleString() : 'N/A'
          }</b>, New events pulled: <b>${
            page.newEventsPulledInLastRun || 0
          }</b>, Existed Events: <b>${page.existedEventPulledInLastRun || 0}</b><br>`
        );
      }

      info.push('<br/>');
      info.push(`<b>Annual Count</b>`);
      for (const key in annual) {
        if (annual[key]) {
          const total =
            annual[key].facebook + annual[key].eventbrite + (annual[key].serpGoogle || 0);
          info.push(
            `<b>${key}</b>: Facebook: ${annual[key].facebook}, Eventbrite: ${
              annual[key].eventbrite
            }, SerpGoogle: ${annual[key].serpGoogle || 0}, similar: ${
              annual[key].similarCount || 0
            }, <b>TOTAL: ${total} - ${annual[key].similarCount || 0} = ${
              total - (annual[key].similarCount || 0)
            }</b>`
          );
        }
      }

      info.push('<br/>');
      info.push(`<b>Approval Count</b>`);
      for (const key in annual) {
        if (annual[key]) {
          info.push(
            `<span style="color:mediumblue;cursor:pointer" onclick="var monthly=document.getElementById('monthly-approved-count-${key}');monthly.style.visibility=monthly.style.visibility==='hidden'?'visible':'hidden';monthly.style.height=monthly.style.height==='0px'?'auto':'0px';monthly.style.overflow=monthly.style.overflow==='hidden'?'visible':'hidden'">${key}</span>: Total approved count: 
            <b>${annual[key].approvedCount.count || 0}</b>, Total: <b>${
              annual[key].yearTotal
            }</b>, Approval Rate: <b>${annual[key].approvedCount.rate}</b>`
          );
          info.push(
            `<div id="monthly-approved-count-${key}" style="visibility: hidden;height:0px;overflow:hidden;margin-left: 19px">${annual[key].approvedCount.monthly}</div>`
          );
        }
      }

      console.log('annual', annual);

      this.modalService.show({
        component: ShowInfoModalComponent,
        providers: [
          { provide: ShowInfoModalActions, useValue: null },
          {
            provide: InfoModalData,
            useValue: new InfoModalData('Event Count - ' + account.userName, null, info),
          },
        ],
        panelClass: 'width-modal-6',
      });
    });
  }

  public menuBar(account: Account, setToNormal: boolean) {
    let actions = <ConfirmModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
        this.accountApiService
          .setMenuBar(account._id, setToNormal)
          .finally(event.complete)
          .subscribe(() => {
            this.fetchAccounts();
          });
      },
    };

    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription: `Are you sure you want to set the menu bar to ${
        setToNormal ? 'normal' : 'simple'
      } style for this account?`,
      confirmModalConfirmButtonText: 'Yes',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  public openClientDashboard(account: Account) {
    this.accountApiService.proxyAccount(account.userName).subscribe((token: string) => {
      const nextDomain = AppModeService.isDevelopmentMode
        ? ConfigService.config.serverDomainV3Local
        : AppModeService.isStaging ? ConfigService.config.serverDomainV3Staging : ConfigService.config.serverDomainV3;
      const url = `${nextDomain}${account.userName}/dashboard/proxy?token=${encodeURIComponent(token)}`;
      window.open(url, '_blank');
    });
  }

  public openEventAggregator() {
    this.accountApiService.createEventAggregatorAdminHandoff().subscribe((token: string) => {
      const nextDomain = AppModeService.isDevelopmentMode
        ? ConfigService.config.serverDomainV3Local
        : AppModeService.isStaging ? ConfigService.config.serverDomainV3Staging : ConfigService.config.serverDomainV3;
      window.open(`${nextDomain}admin/event-aggregator?token=${encodeURIComponent(token)}`, '_blank');
    });
  }

  private fetchAccounts() {
    this.accountApiService.getAccounts().subscribe((accounts) => {
      this.accounts = accounts;
      this.liveCount = accounts.filter((x) => x.status === 'live' && !x.isAdministrator).length;
      this.deadCount = accounts.filter((x) => x.status === 'dead').length;
    });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/admin/accounts/accounts.component.ts